import React, { useState } from "react";
import { Popover, Drawer } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useNavigate } from "react-router-dom";
import { useGlobal } from "../context/global-context";
import WineBottle from "./wine-bottle";
import { resolveProduct, money } from "../pages/wine-store/inventory";
import "../styles/wine-theme.css";
import "./cart-icon.css";

export default function CartIcon() {
  const { globalState, removeFromCart, addToCart, updateCartItemQuantity } = useGlobal();
  const navigate = useNavigate();
  const [opened, setOpened] = useState(false);
  const isMobile = useMediaQuery("(max-width: 767px)");

  const decrementItem = (item) => {
    if (item.quantity > 1) {
      updateCartItemQuantity(item.name, item.quantity - 1);
    } else {
      removeFromCart(item.name);
    }
  };

  const totalItems = globalState.cart.reduce((total, item) => total + item.quantity, 0);

  const handleCheckout = () => {
    setOpened(false);
    navigate("/checkout");
  };

  const trigger = (
    <button
      type="button"
      className={`vt-wine vt-cart-trigger${isMobile ? " vt-cart-trigger--icon" : ""}`}
      onClick={() => setOpened((o) => !o)}
      aria-label={`Cart, ${totalItems} items`}
    >
      <CartGlyph />
      {!isMobile && <span>Cart</span>}
      {totalItems > 0 && <span className="vt-cart-trigger__count">{totalItems}</span>}
    </button>
  );

  const body = (
    <div className="vt-wine vt-cart-panel">
      <div className="vt-cart-panel__head">
        <div className="vt-cart-panel__title">
          <h2>Your cart</h2>
          <span className="vt-mono">{totalItems} {totalItems === 1 ? "item" : "items"}</span>
        </div>
        {isMobile && (
          <button type="button" className="vt-cart-panel__close" aria-label="Close cart" onClick={() => setOpened(false)}>
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M4 4l8 8M12 4l-8 8" stroke="#0C0E0E" strokeWidth="1.8" strokeLinecap="round" /></svg>
          </button>
        )}
      </div>

      {globalState.cart.length === 0 ? (
        <p className="vt-cart-panel__empty">Your cart is empty.</p>
      ) : (
        <>
          {globalState.cart.map((raw) => {
            const item = resolveProduct(raw);
            return (
              <div key={item.name} className="vt-cart-line">
                <div className="vt-thumb vt-cart-line__thumb" style={{ background: item.tint }}>
                  <WineBottle fill={item.fill} width={isMobile ? 28 : 24} />
                </div>
                <div className="vt-cart-line__body">
                  <div className="vt-cart-line__row">
                    <span className="vt-cart-line__name">{item.name}</span>
                    <span className="vt-price">{money(parseFloat(item.cost) * item.quantity)}</span>
                  </div>
                  <div className="vt-cart-line__row">
                    <div className="vt-stepper">
                      <button type="button" aria-label="Decrease quantity" onClick={() => decrementItem(item)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3.5 7h7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      </button>
                      <span>{item.quantity}</span>
                      <button type="button" aria-label="Increase quantity" onClick={() => addToCart(item)}>
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none" aria-hidden="true"><path d="M3.5 7h7M7 3.5v7" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" /></svg>
                      </button>
                    </div>
                    <button type="button" className="vt-remove" aria-label={`Remove ${item.name}`} onClick={() => removeFromCart(item.name)}>
                      <TrashGlyph />
                    </button>
                  </div>
                </div>
              </div>
            );
          })}

          <div className="vt-divider" />

          <div className="vt-cart-panel__total">
            <span>Total</span>
            <span className="vt-price">{money(globalState.cartTotal)}</span>
          </div>

          <button type="button" className="vt-btn vt-btn--primary vt-btn--lg vt-btn--block" onClick={handleCheckout}>
            Go to checkout
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true"><path d="M3 8h9.4M9 4.6 12.6 8 9 11.4" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" /></svg>
          </button>

          <span className="vt-cart-panel__note">Age is verified in the next step.</span>
        </>
      )}
    </div>
  );

  if (isMobile) {
    return (
      <>
        {trigger}
        <Drawer
          opened={opened}
          onClose={() => setOpened(false)}
          position="bottom"
          size="auto"
          withCloseButton={false}
          overlayProps={{ color: "#090B0B", backgroundOpacity: 0.55 }}
          styles={{
            content: { borderRadius: "22px 22px 0 0", background: "#FFFFFF", height: "auto", maxHeight: "88dvh", width: "100%" },
            body: { padding: 0 },
          }}
        >
          <div className="vt-wine vt-cart-sheet">
            <div className="vt-cart-sheet__handle" />
            {body}
          </div>
        </Drawer>
      </>
    );
  }

  return (
    <Popover
      width={372}
      position="bottom-end"
      offset={12}
      opened={opened}
      onChange={setOpened}
      styles={{
        dropdown: {
          padding: 22,
          borderRadius: 16,
          border: "1px solid #E4E7E6",
          boxShadow: "0 20px 48px rgba(12, 14, 14, 0.14)",
        },
      }}
    >
      <Popover.Target>{trigger}</Popover.Target>
      <Popover.Dropdown>{body}</Popover.Dropdown>
    </Popover>
  );
}

function CartGlyph() {
  return (
    <svg width="17" height="17" viewBox="0 0 18 18" fill="none" aria-hidden="true">
      <path d="M1.8 2.2h2.1l1.9 8.4h7.6l1.6-6H5.2" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" />
      <circle cx="7" cy="14.4" r="1.2" fill="currentColor" />
      <circle cx="12.8" cy="14.4" r="1.2" fill="currentColor" />
    </svg>
  );
}

function TrashGlyph() {
  return (
    <svg width="16" height="16" viewBox="0 0 16 16" fill="none" aria-hidden="true">
      <path d="M2.8 4.3h10.4M6.4 4.3V3.1h3.2v1.2M4.4 4.3l.6 8.2c0 .7.5 1.2 1.2 1.2h3.6c.7 0 1.2-.5 1.2-1.2l.6-8.2" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  );
}
